// frontend/src/components/Map.js
import React, { useState, useRef, useCallback } from 'react';
import { GoogleMap, Marker, Circle, InfoWindow, OverlayView, useLoadScript } from '@react-google-maps/api';
import PropTypes from 'prop-types';
import styles from './Map.module.css';
import { FaCrosshairs, FaStar, FaMapMarkerAlt, FaUsers, FaFutbol } from 'react-icons/fa';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const options = {
  disableDefaultUI: true,
  zoomControl: true,
};

// Sport color mapping
const sportColors = {
  Soccer: '#4CAF50',
  Football: '#4CAF50',
  Basketball: '#FF5722',
  Baseball: '#FF9800',
  Tennis: '#9C27B0',
  Volleyball: '#E91E63',
  TableTennis: '#2196F3',
  Cricket: '#795548',
  Badminton: '#607D8B',
  Hockey: '#3F51B5',
  Swimming: '#00BCD4',
  Running: '#8BC34A',
  Boxing: '#F44336',
  Karate: '#FF5722',
  Golf: '#4CAF50',
  Gym: '#9E9E9E',
  Fitness: '#9E9E9E',
  Lacrosse: '#3F51B5',
};

// Get sport symbol/emoji
const getSportSymbol = (sport) => {
  const sportSymbols = {
    Soccer: '⚽',
    Football: '⚽',
    Basketball: '🏀',
    Baseball: '⚾',
    Tennis: '🎾',
    Volleyball: '🏐',
    TableTennis: '🏓',
    Cricket: '🏏',
    Badminton: '🏸',
    Hockey: '🏒',
    Swimming: '🏊',
    Running: '🏃',
    Boxing: '🥊',
    Karate: '🥋',
    Golf: '⛳',
    Gym: '🏋️',
    Fitness: '💪',
    Lacrosse: '🥍',
  };
  
  return sportSymbols[sport] || '⚽';
};

// Convert miles to meters
const milesToMeters = (miles) => miles * 1609.34;

// Circle options for range visualization
const circleOptions = {
  strokeColor: '#A4EB3F',
  strokeOpacity: 0.6,
  strokeWeight: 2,
  fillColor: '#A4EB3F',
  fillOpacity: 0.1,
  clickable: false,
  draggable: false,
  editable: false,
  visible: true,
  zIndex: 1,
};

// Custom Pin Marker Component
const CustomPinMarker = ({ club, onClick, onMouseEnter, onMouseLeave, hasImageError, onImageError }) => {
  const sportColor = sportColors[club.sport] || '#A4EB3F';
  const hasLogo = club.clubLogo && !hasImageError;

  return (
    <div
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        cursor: 'pointer',
        transform: 'translate(-50%, -100%)',
        filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.4))',
        transition: 'transform 0.2s ease',
      }}
      onMouseOver={(e) => e.currentTarget.style.transform = 'translate(-50%, -100%) scale(1.1)'}
      onMouseOut={(e) => e.currentTarget.style.transform = 'translate(-50%, -100%)'}
    >
      <svg width="50" height="60" viewBox="0 0 50 60">
        <defs>
          <clipPath id={`clip-${club._id}`}>
            <circle cx="25" cy="22" r="17" />
          </clipPath>
        </defs>
        {/* Pin shape */}
        <path
          d="M25 58 L10 32 Q2 18 10 8 Q17 0 25 0 Q33 0 40 8 Q48 18 40 32 Z"
          fill={sportColor}
        />
        {/* White border circle */}
        <circle cx="25" cy="22" r="20" fill="white" />
        {/* Inner circle background */}
        <circle cx="25" cy="22" r="17" fill={hasLogo ? '#1a1a1a' : sportColor} />
      </svg>
      
      {/* Logo or Sport Icon overlay */}
      <div
        style={{
          position: 'absolute',
          top: '5px',
          left: '8px',
          width: '34px',
          height: '34px',
          borderRadius: '50%',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: hasLogo ? '#1a1a1a' : sportColor,
        }}
      >
        {hasLogo ? (
          <img
            src={club.clubLogo}
            alt={club.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={onImageError}
          />
        ) : (
          <span style={{ fontSize: '18px' }}>{getSportSymbol(club.sport)}</span>
        )}
      </div>
    </div>
  );
};

// Info Window custom styles
const infoWindowStyles = {
  container: {
    padding: '12px',
    maxWidth: '280px',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  logo: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #A4EB3F',
  },
  logoFallback: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #A4EB3F 0%, #8BC726 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
  },
  name: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0,
    lineHeight: '1.2',
  },
  sport: {
    fontSize: '12px',
    color: '#666',
    margin: 0,
  },
  meta: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
    flexWrap: 'wrap',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: '#555',
  },
  description: {
    fontSize: '12px',
    color: '#666',
    marginTop: '8px',
    lineHeight: '1.4',
    borderTop: '1px solid #eee',
    paddingTop: '8px',
  },
  viewButton: {
    marginTop: '10px',
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #A4EB3F 0%, #8BC726 100%)',
    color: '#111',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
};

const Map = ({ clubs, userLocation, onClubMarkerClick, rangeInMiles, onRangeChange, showRangeControl, onLocationChange }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [zipcode, setZipcode] = useState('');
  const [center, setCenter] = useState({ lat: userLocation.lat, lng: userLocation.lng });
  const [hoveredClub, setHoveredClub] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [isZipcodeExpanded, setIsZipcodeExpanded] = useState(false);
  const mapRef = useRef();
  const isDraggingRef = useRef(false);

  React.useEffect(() => {
    setCenter({ lat: userLocation.lat, lng: userLocation.lng });
  }, [userLocation]);

  const handleZipcodeChange = (e) => {
    setZipcode(e.target.value);
  };

  const handleZipcodeSubmit = async (e) => {
    e.preventDefault();
    if (!zipcode) return;
    
    // Use Google Maps JavaScript API Geocoder instead of REST API
    // This works with website restrictions, unlike the REST API
    if (!window.google || !window.google.maps) {
      alert('Google Maps not loaded. Please refresh the page.');
      return;
    }
    
    try {
      const geocoder = new window.google.maps.Geocoder();
      
      geocoder.geocode({ address: zipcode }, (results, status) => {
        console.log('Geocoding response:', status, results);
        
        if (status === 'OK' && results && results.length > 0) {
          const location = results[0].geometry.location;
          const newLocation = { lat: location.lat(), lng: location.lng() };
          setCenter(newLocation);
          if (mapRef.current) {
            mapRef.current.panTo(location);
          }
          // Notify parent about location change to refetch clubs
          if (onLocationChange) {
            onLocationChange(newLocation);
          }
        } else {
          console.error('Geocoding failed:', status);
          alert(`Could not find location: ${status}`);
        }
      });
    } catch (err) {
      console.error('Geocoding error:', err);
      alert(`Error finding zipcode: ${err.message}`);
    }
  };

  // Get user's current location using browser geolocation
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        console.log('Got user location:', newLocation);
        
        // Update map center
        setCenter(newLocation);
        if (mapRef.current) {
          mapRef.current.panTo(newLocation);
        }
        
        // Notify parent about location change to refetch clubs
        if (onLocationChange) {
          onLocationChange(newLocation);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Unable to get your location. ';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please allow location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out.';
            break;
          default:
            errorMessage += 'An unknown error occurred.';
        }
        
        alert(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleRangeSliderChange = useCallback((e) => {
    const newRange = parseInt(e.target.value, 10);
    if (onRangeChange) {
      onRangeChange(newRange);
    }
  }, [onRangeChange]);

  // Calculate appropriate zoom level based on range
  const getZoomForRange = useCallback((miles) => {
    if (miles <= 10) return 12;
    if (miles <= 25) return 11;
    if (miles <= 50) return 10;
    if (miles <= 100) return 9;
    return 8;
  }, []);

  // Update zoom when range changes
  React.useEffect(() => {
    if (mapRef.current && rangeInMiles) {
      const newZoom = getZoomForRange(rangeInMiles);
      mapRef.current.setZoom(newZoom);
    }
  }, [rangeInMiles, getZoomForRange]);

  // Handle marker hover
  const handleMarkerMouseOver = useCallback((club) => {
    setHoveredClub(club);
  }, []);

  const handleMarkerMouseOut = useCallback(() => {
    setHoveredClub(null);
  }, []);

  // Handle image error
  const handleImageError = useCallback((clubId) => {
    setImageErrors(prev => ({ ...prev, [clubId]: true }));
  }, []);

  // Handle map drag start
  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  // Handle map idle (after drag or zoom)
  const handleMapIdle = useCallback(() => {
    if (!isDraggingRef.current || !mapRef.current) return;
    isDraggingRef.current = false;
    
    const newCenter = mapRef.current.getCenter();
    if (newCenter) {
      const newLocation = { lat: newCenter.lat(), lng: newCenter.lng() };
      setCenter(newLocation);
      // Notify parent about location change to refetch clubs
      if (onLocationChange) {
        onLocationChange(newLocation);
      }
    }
  }, [onLocationChange]);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading Maps...</div>;

  const validClubs = clubs.filter(
    club => club.location && Array.isArray(club.location.coordinates) && club.location.coordinates.length === 2
  );

  return (
    <div className={styles.mapContainer}>
      {!isZipcodeExpanded ? (
        <button
          type="button"
          className={styles.locationIconButton}
          onClick={() => setIsZipcodeExpanded(true)}
          title="Search by location"
        >
          <FaCrosshairs />
        </button>
      ) : (
        <form className={styles.zipcodeForm} onSubmit={handleZipcodeSubmit}>
          <input
            type="text"
            placeholder="Enter zipcode"
            value={zipcode}
            onChange={handleZipcodeChange}
            className={styles.zipcodeInput}
            autoFocus
          />
          <button type="submit" className={styles.zipcodeButton}>Go</button>
          <button
            type="button"
            className={styles.zipcodeButton}
            onClick={handleGetCurrentLocation}
            title="Get my current location"
          >
            <FaCrosshairs />
          </button>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => {
              setIsZipcodeExpanded(false);
              setZipcode('');
            }}
            title="Close"
          >
            ✕
          </button>
        </form>
      )}

      {/* Range Slider Control */}
      {showRangeControl && (
        <div className={styles.rangeControl}>
          <label className={styles.rangeLabel}>
            <span className={styles.rangeLabelText}>Radius</span>
            <span className={styles.rangeValue}>{rangeInMiles} mi</span>
          </label>
          <input
            type="range"
            min="5"
            max="100"
            step="5"
            value={rangeInMiles}
            onChange={handleRangeSliderChange}
            className={styles.rangeSlider}
            style={{
              background: `linear-gradient(90deg, 
                rgba(164, 235, 63, 0.7) 0%, 
                rgba(164, 235, 63, 0.5) ${((rangeInMiles - 5) / 95) * 100}%, 
                rgba(164, 235, 63, 0.15) ${((rangeInMiles - 5) / 95) * 100}%, 
                rgba(164, 235, 63, 0.15) 100%)`
            }}
          />
          <div className={styles.rangeMarks}>
            <span>5</span>
            <span>50</span>
            <span>100</span>
          </div>
        </div>
      )}

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={getZoomForRange(rangeInMiles || 50)}
        center={center}
        options={options}
        onLoad={map => (mapRef.current = map)}
        onDragStart={handleDragStart}
        onIdle={handleMapIdle}
      >
        {/* User Location Marker */}
        <Marker
          position={center}
          icon={{
            url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
          }}
        />

        {/* Range Circle */}
        {showRangeControl && rangeInMiles && (
          <Circle
            center={center}
            radius={milesToMeters(rangeInMiles)}
            options={circleOptions}
          />
        )}

        {/* Custom Club Markers using OverlayView */}
        {validClubs.map((club) => {
          const position = {
            lat: club.location.coordinates[1],
            lng: club.location.coordinates[0],
          };

          return (
            <OverlayView
              key={club._id}
              position={position}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <CustomPinMarker
                club={club}
                onClick={() => onClubMarkerClick && onClubMarkerClick(club)}
                onMouseEnter={() => handleMarkerMouseOver(club)}
                onMouseLeave={handleMarkerMouseOut}
                hasImageError={imageErrors[club._id]}
                onImageError={() => handleImageError(club._id)}
              />
            </OverlayView>
          );
        })}

        {/* Info Window on Hover */}
        {hoveredClub && hoveredClub.location && (
          <InfoWindow
            position={{
              lat: hoveredClub.location.coordinates[1],
              lng: hoveredClub.location.coordinates[0],
            }}
            options={{
              pixelOffset: new window.google.maps.Size(0, -65),
              disableAutoPan: true,
            }}
            onCloseClick={() => setHoveredClub(null)}
          >
            <div style={infoWindowStyles.container}>
              <div style={infoWindowStyles.header}>
                {hoveredClub.clubLogo && !imageErrors[hoveredClub._id] ? (
                  <img
                    src={hoveredClub.clubLogo}
                    alt={hoveredClub.name}
                    style={infoWindowStyles.logo}
                    onError={() => handleImageError(hoveredClub._id)}
                  />
                ) : (
                  <div style={infoWindowStyles.logoFallback}>
                    {getSportSymbol(hoveredClub.sport)}
                  </div>
                )}
                <div>
                  <h4 style={infoWindowStyles.name}>{hoveredClub.name}</h4>
                  <p style={infoWindowStyles.sport}>{hoveredClub.sport}</p>
                </div>
              </div>
              
              <div style={infoWindowStyles.meta}>
                {hoveredClub.rating !== undefined && hoveredClub.rating !== null && (
                  <span style={infoWindowStyles.metaItem}>
                    <FaStar style={{ color: '#F59E0B' }} />
                    {Number(hoveredClub.rating).toFixed(1)}
                  </span>
                )}
                {hoveredClub.distance !== null && hoveredClub.distance !== undefined && (
                  <span style={infoWindowStyles.metaItem}>
                    <FaMapMarkerAlt style={{ color: '#A4EB3F' }} />
                    {hoveredClub.distance} mi
                  </span>
                )}
                {hoveredClub.memberCount && (
                  <span style={infoWindowStyles.metaItem}>
                    <FaUsers style={{ color: '#666' }} />
                    {hoveredClub.memberCount} members
                  </span>
                )}
              </div>

              {hoveredClub.description && (
                <p style={infoWindowStyles.description}>
                  {hoveredClub.description.length > 100
                    ? `${hoveredClub.description.substring(0, 100)}...`
                    : hoveredClub.description}
                </p>
              )}

              <button
                style={infoWindowStyles.viewButton}
                onClick={() => onClubMarkerClick && onClubMarkerClick(hoveredClub)}
              >
                View Club Details
              </button>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
};

Map.propTypes = {
  clubs: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      sport: PropTypes.string.isRequired,
      location: PropTypes.shape({
        type: PropTypes.string.isRequired,
        coordinates: PropTypes.arrayOf(PropTypes.number).isRequired,
      }),
      rating: PropTypes.number,
      nextGameTime: PropTypes.string,
      introduction: PropTypes.string,
      address: PropTypes.string,
      clubLogo: PropTypes.string,
      description: PropTypes.string,
      distance: PropTypes.number,
      memberCount: PropTypes.number,
    })
  ).isRequired,
  userLocation: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }).isRequired,
  onClubMarkerClick: PropTypes.func,
  rangeInMiles: PropTypes.number,
  onRangeChange: PropTypes.func,
  showRangeControl: PropTypes.bool,
  onLocationChange: PropTypes.func,
};

Map.defaultProps = {
  clubs: [],
  userLocation: { lat: 0, lng: 0 },
  rangeInMiles: 50,
  showRangeControl: true,
};

export default Map;
